// src/components/games/pipsWordPuzzle/PipsWordPuzzleStats.tsx
// FIXED: Using database storage instead of localStorage

import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonAlert,
  IonSpinner,
} from '@ionic/react';
import { trashOutline, trophy } from 'ionicons/icons';
import { getStats, clearAllProgress, getLeaderboard, getPersonalBest, getUserRank, PipsWordPuzzleScore } from '../../../services/pipsWordPuzzleStorageService';
import { GameStats } from './types';
import { useIonViewWillEnter } from '@ionic/react';
import './PipsWordPuzzleStats.css';

export const PipsWordPuzzleStats: React.FC = () => {
  // FIXED: Load stats from database async
  const [stats, setStats] = useState<GameStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showResetAlert, setShowResetAlert] = useState(false);
  const [leaderboard, setLeaderboard] = useState<PipsWordPuzzleScore[]>([]);
  const [personalBest, setPersonalBest] = useState<number>(0);
  const [userRank, setUserRank] = useState<number>(0);

  // FIXED: Load stats on mount
  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      const dbStats = await getStats();
      setStats(dbStats);
      setLoading(false);
    };

    loadStats();
  }, []);

  // Load leaderboard data when view is entered
  useIonViewWillEnter(() => {
    const loadLeaderboardData = async () => {
      const [leaders, bestScore, rank] = await Promise.all([
        getLeaderboard(10),
        getPersonalBest(),
        getUserRank(),
      ]);
      setLeaderboard(leaders);
      setPersonalBest(bestScore);
      setUserRank(rank);
    };

    loadLeaderboardData();
  });

  // FIXED: Handle reset with async
  const handleReset = async () => {
    await clearAllProgress();
    const freshStats = await getStats();
    setStats(freshStats);
    setShowResetAlert(false);
  };

  if (loading || !stats) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/games/pips-word-puzzle" />
            </IonButtons>
            <IonTitle>📊 Statistics</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/games/pips-word-puzzle" />
          </IonButtons>
          <IonTitle>📊 Statistics</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="pips-word-puzzle-stats-container">
          {/* Header */}
          <div className="stats-header">
            <h1>Your Pip's Word Puzzle Stats</h1>
            <p>Track your progress and achievements</p>
          </div>

          {/* Main Stats Grid */}
          <div className="stats-main-grid">
            <IonCard className="stat-card points-card">
              <IonCardContent>
                <div className="stat-icon">💰</div>
                <div className="stat-number">{stats.points}</div>
                <div className="stat-title">Points</div>
                <div className="stat-description">Total earned</div>
              </IonCardContent>
            </IonCard>

            <IonCard className="stat-card streak-card">
              <IonCardContent>
                <div className="stat-icon">🔥</div>
                <div className="stat-number">{stats.currentStreak}</div>
                <div className="stat-title">Current Streak</div>
                <div className="stat-description">Days in a row</div>
              </IonCardContent>
            </IonCard>

            <IonCard className="stat-card wins-card">
              <IonCardContent>
                <div className="stat-icon">🏆</div>
                <div className="stat-number">{stats.gamesWon}</div>
                <div className="stat-title">Wins</div>
                <div className="stat-description">Games won</div>
              </IonCardContent>
            </IonCard>

            <IonCard className="stat-card games-card">
              <IonCardContent>
                <div className="stat-icon">🎮</div>
                <div className="stat-number">{stats.gamesPlayed}</div>
                <div className="stat-title">Games Played</div>
                <div className="stat-description">Total puzzles</div>
              </IonCardContent>
            </IonCard>
          </div>

          {/* Performance Stats */}
          <IonCard className="performance-card">
            <IonCardHeader>
              <IonCardTitle>Performance</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="performance-grid">
                <div className="performance-stat">
                  <div className="performance-label">Win Rate</div>
                  <div className="performance-value">
                    {stats.gamesPlayed > 0
                      ? `${Math.round((stats.gamesWon / stats.gamesPlayed) * 100)}%`
                      : '0%'}
                  </div>
                </div>
                <div className="performance-stat">
                  <div className="performance-label">Max Streak</div>
                  <div className="performance-value">{stats.maxStreak}</div>
                </div>
                <div className="performance-stat">
                  <div className="performance-label">Avg Points</div>
                  <div className="performance-value">
                    {stats.gamesPlayed > 0
                      ? Math.round(stats.points / stats.gamesPlayed)
                      : 0}
                  </div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Guess Distribution */}
          <IonCard className="distribution-card">
            <IonCardHeader>
              <IonCardTitle>Guess Distribution</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="distribution-bars">
                {stats.guessDistribution.map((count, index) => {
                  const maxCount = Math.max(...stats.guessDistribution, 1);
                  const percentage = (count / maxCount) * 100;
                  return (
                    <div key={index} className="distribution-row">
                      <div className="guess-number">{index + 1}</div>
                      <div className="bar-container">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${Math.max(percentage, 5)}%`,
                            background: count > 0
                              ? `linear-gradient(90deg, var(--ion-color-primary) 0%, var(--ion-color-primary-tint) 100%)`
                              : '#ddd'
                          }}
                        >
                          {count > 0 && <span className="bar-count">{count}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {stats.gamesPlayed === 0 && (
                <p className="no-data-message">
                  Play some games to see your guess distribution!
                </p>
              )}
            </IonCardContent>
          </IonCard>

          {/* Achievements */}
          <IonCard className="achievements-card">
            <IonCardHeader>
              <IonCardTitle>🎖️ Achievements</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="achievements-grid">
                <div className={`achievement ${stats.gamesPlayed >= 1 ? 'unlocked' : 'locked'}`}>
                  <div className="achievement-icon">🎯</div>
                  <div className="achievement-name">First Try</div>
                  <div className="achievement-desc">Play your first game</div>
                </div>

                <div className={`achievement ${stats.gamesWon >= 5 ? 'unlocked' : 'locked'}`}>
                  <div className="achievement-icon">⭐</div>
                  <div className="achievement-name">Winner</div>
                  <div className="achievement-desc">Win 5 games</div>
                </div>

                <div className={`achievement ${stats.currentStreak >= 3 ? 'unlocked' : 'locked'}`}>
                  <div className="achievement-icon">🔥</div>
                  <div className="achievement-name">On Fire</div>
                  <div className="achievement-desc">3 day win streak</div>
                </div>

                <div className={`achievement ${stats.points >= 100 ? 'unlocked' : 'locked'}`}>
                  <div className="achievement-icon">💎</div>
                  <div className="achievement-name">Point Master</div>
                  <div className="achievement-desc">Earn 100 points</div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Leaderboard Card */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={trophy} /> Top Players
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {personalBest > 0 && (
                <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(var(--ion-color-primary-rgb), 0.1)', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '14px', color: 'var(--ion-color-medium)', marginBottom: '4px' }}>Your Personal Best</div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--ion-color-primary)' }}>{personalBest} points</div>
                    </div>
                    {userRank > 0 && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '14px', color: 'var(--ion-color-medium)', marginBottom: '4px' }}>Your Rank</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--ion-color-success)' }}>#{userRank}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {leaderboard.length > 0 ? (
                <div className="leaderboard-list">
                  {leaderboard.map((entry, index) => (
                    <div key={entry.id} className="leaderboard-entry">
                      <div className="rank-badge">
                        {index === 0 && '🥇'}
                        {index === 1 && '🥈'}
                        {index === 2 && '🥉'}
                        {index > 2 && `#${index + 1}`}
                      </div>
                      <div className="player-info">
                        <div className="player-name">
                          {entry.display_name || entry.username || 'Anonymous'}
                        </div>
                        <div className="player-stats">
                          <span className="score">{entry.score.toLocaleString()} pts</span>
                          <span className="guesses">{entry.won ? `✓ ${entry.guesses_used} guesses` : '✗ Lost'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: 'var(--ion-color-medium)' }}>
                  No scores yet. Be the first to set a record!
                </p>
              )}
            </IonCardContent>
          </IonCard>

          {/* Reset Button */}
          {stats.gamesPlayed > 0 && (
            <IonCard style={{ margin: '0', background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              <IonCardContent style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '14px', color: 'var(--ion-color-medium)', marginBottom: '12px' }}>
                  Want to start fresh?
                </div>
                <IonButton
                  color="danger"
                  fill="outline"
                  onClick={() => setShowResetAlert(true)}
                >
                  <IonIcon icon={trashOutline} slot="start" />
                  Reset All Progress
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}
        </div>

        {/* Reset Confirmation Alert */}
        <IonAlert
          isOpen={showResetAlert}
          onDidDismiss={() => setShowResetAlert(false)}
          header="Reset Progress?"
          message="This will permanently delete all your stats from the database, completed puzzles, and game history. This action cannot be undone!"
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              cssClass: 'secondary',
            },
            {
              text: 'Reset Everything',
              role: 'destructive',
              handler: handleReset,
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};